import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Switch,
  Grid,
  Typography,
  Alert,
  Box,
  InputAdornment,
  Stack
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { fetchCategorias, fetchProductosFinancieros } from '../../../../utils/generalsFetch';
import { data } from 'react-router-dom';
import Selector from '../../../../components/Selector';
import { formatNumberWithCommas } from '../../../../utils/generalFunctions';
import { el } from 'date-fns/locale';


const OPciones = {
  aplicaA: [
    { value: 'producto', label: 'Producto' },
    { value: 'prestamo', label: 'Préstamo' }
  ],
  tipo: [
    { value: 'general', label: 'General (catálogo)' },
    { value: 'especifico', label: 'Específico (asignable a un préstamo)' }
  ],
  modoCalculo: [
    { value: 'fijo', label: 'Fijo' },
    { value: 'porcentaje', label: 'Porcentaje' }
  ],
  basePorcentaje: [
    { value: 'monto_financiado', label: 'Monto financiado' },
    { value: 'saldo', label: 'Saldo' },
    { value: 'precio_producto', label: 'Precio del producto' }
  ],
  moneda: [
    { value: 'DOP', label: 'DOP' },
    { value: 'USD', label: 'USD' }
  ]
};

export default function CrearEditarCategoriaDialog({ open, onClose, onSuccess, categoria }) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [nombreError, setNombreError] = useState('');
  const [errors, setErrors] = useState({});
  const [productosFinancieros, setProductosFinancieros] = useState([]);
  const [categoriasProducto, setCategoriasProducto] = useState([]);
  const [productosFinancierosSelected, setProductosFinancierosSelected] = useState([]);
  const [categoriasProductoSelected, setCategoriasProductoSelected] = useState([]);
  const [montoFijo, setMontoFijo] = useState('0.00');
  const isEditing = !!categoria;

  // Form state
  const [formData, setFormData] = useState({
    nombre: '',
    aplicaA: 'prestamo',
    tipo: 'general',
    requiereGestionOperativa: false,
    activo: true,
    descripcion: ''
  });

  // Handle input changes
  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    
    // Clear error for this field when user starts typing
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    }
  };

  useEffect(() => {
    fetchProductosFinancieros({ idcompany: localStorage.getItem('idcompany'), usuarioId: localStorage.getItem('id') }).then(data => {
      setProductosFinancieros(data)
    });
    fetchCategorias({ idcompany: localStorage.getItem('idcompany'), usuarioId: localStorage.getItem('id') }).then(data => {
      setCategoriasProducto(data)
    })
  }, []);

  const validateForm = (data) => {
    const errors = {};
  
    // Nombre validation
    if (!data.nombre || data.nombre.trim().length < 3) {
      errors.nombre = 'El nombre debe tener al menos 3 caracteres';
    } else if (data.nombre.trim().length > 60) {
      errors.nombre = 'El nombre no puede exceder 60 caracteres';
    }
  
    // AplicaA validation
    if (!data.aplicaA) {
      errors.aplicaA = 'Debe seleccionar a qué aplica';
    }
  
    //Categorias de producto validation
    if (data.aplicaA === 'producto' && categoriasProductoSelected.length === 0) {
      errors.categoriasProducto = 'Debe seleccionar al menos una categoría de producto';
    }
  
    //Productos financieros validation
    if (data.aplicaA === 'prestamo' && productosFinancierosSelected.length === 0) {
      errors.productosFinancieros = 'Debe seleccionar al menos un producto financiero';
    }
  
    // MontoFijo validation (if modo = fijo)
    if (!montoFijo || montoFijo <= 0) {
      errors.montoFijo = 'El monto fijo es requerido y debe ser mayor a 0';
    }
  
    return errors;
  };
  

  // Reset form when dialog opens/closes or categoria changes
  useEffect(() => {
    if (open) {
      if (categoria) {
        // Editing mode
        setFormData({
          nombre: categoria.nombre,
          aplicaA: categoria.aplicaA,
          tipo: categoria.tipo || 'general',
          requiereGestionOperativa: categoria.requiereGestionOperativa,
          activo: categoria.activo,
          descripcion: categoria.descripcion || ''
        });
        setProductosFinancierosSelected(JSON.parse(categoria.categoria_prestamo).map(producto => producto.id));
        setCategoriasProductoSelected(JSON.parse(categoria.categoria_producto).map(categoria => categoria.id));
        setMontoFijo(categoria.montoFijo);
      } else {
        // Create mode
        setFormData({
          nombre: '',
          aplicaA: 'prestamo',
          tipo: 'general',
          requiereGestionOperativa: false,
          activo: true,
          descripcion: ''
        });
      }
      setError('');
      setNombreError('');
      setErrors({});
    }
  }, [open, categoria]);

  // Validate nombre uniqueness (simulate async validation)
  useEffect(() => {
    const validateNombre = async () => {
      if (formData.nombre && formData.nombre.length >= 3) {
        // Simulate API call to check uniqueness
        await new Promise(resolve => setTimeout(resolve, 500));
        const isDuplicate = formData.nombre.toLowerCase() === 'matrícula' && !isEditing;
        if (isDuplicate) {
          setNombreError('Este nombre ya existe');
        } else {
          setNombreError('');
        }
      }
    };

    const timeoutId = setTimeout(validateNombre, 300);
    return () => clearTimeout(timeoutId);
  }, [formData.nombre, isEditing]);

  const onSubmit = async (e) => {
    
    if (nombreError) {
      setError('El nombre debe ser único');
      return;
    }

    // Validate form
    const validationErrors = validateForm(formData);
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }


    setLoading(true);
    setError(null);

    if(isEditing){
      await updateCategoria(categoria.id, formData);
    }else{
      await createCategoria(formData);
    }
  
  };

  const createCategoria = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_SERVER_URL}/api/categorias_cargos/${localStorage.getItem('idcompany')}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Usuario-ID': localStorage.getItem('id'),
          'X-company-ID': localStorage.getItem('idcompany')
        },
        body: JSON.stringify({
          nombre: formData.nombre,
          aplicaA: formData.aplicaA,
          tipo: formData.aplicaA === 'prestamo' ? formData.tipo : 'general',
          categoria_producto: categoriasProductoSelected.map(categoria => ({id: categoria})),
          categoria_prestamo: productosFinancierosSelected.map(producto => ({id: producto})),
          montoFijo: parseFloat(montoFijo),
          requiereGestionOperativa: formData.requiereGestionOperativa,
          moneda: 'DOP',
          activo: true,
          descripcion: formData.descripcion,
          created_by: localStorage.getItem('id')
        })
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Error al crear la categoría');
      }
      
      const data = await response.json();
      onSuccess(data);
    } catch (error) {
      alert(JSON.stringify(error));
      console.error('Error al crear la categoría:', error);
      setError(error.message || 'Error al crear la categoría');
    } finally {
      setLoading(false);
    }

  };

  const updateCategoria = async (id, data) => {
    try {
      const response = await fetch(`${process.env.REACT_APP_SERVER_URL}/api/categorias_cargos/${id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'X-Usuario-ID': localStorage.getItem('id'),
          'X-company-ID': localStorage.getItem('idcompany')
        },
        body: JSON.stringify({
          nombre: formData.nombre,
          aplicaA: formData.aplicaA,
          tipo: formData.aplicaA === 'prestamo' ? formData.tipo : 'general',
          categoria_producto: categoriasProductoSelected.map(categoria => ({id: categoria})),
          categoria_prestamo: productosFinancierosSelected.map(producto => ({id: producto})),
          montoFijo: parseFloat(montoFijo),
          requiereGestionOperativa: formData.requiereGestionOperativa,
          moneda: 'DOP',
          activo: true,
          descripcion: formData.descripcion,
          created_by: localStorage.getItem('id')
        })
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Error al actualizar la categoría');
      }
      
      const data = await response.json();
      onSuccess(data);
    } catch (error) {
      console.error('Error al actualizar la categoría:', error);
      setError(error.message || 'Error al actualizar la categoría');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      onClose();
    }
  };

  return (
    <Dialog 
      open={open} 
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: { borderRadius: 3 }
      }}
    >
      <DialogTitle>
        <Typography variant="h6" fontWeight={600}>
          {isEditing ? 'Editar categoría' : 'Nueva categoría de cargo'}
        </Typography>
      </DialogTitle>

      <form onSubmit={onSubmit}>
        <DialogContent>
          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <Grid container spacing={3}>
              {/* Error Alert */}
              {(error || Object.keys(errors).length > 0) && (
                <Grid item xs={12}>
                  <Alert severity="error" sx={{ mb: 2 }}>
                    {error || 'Por favor corrige los errores en el formulario'}
                  </Alert>
                </Grid>
              )}

              {/* Column 1 */}
              <Grid item size={12}>
                <Stack >
                  {/* Nombre */}
                  <Typography fontWeight={600} sx={{ml: 1}}>Nombre</Typography>
                  <TextField
                    fullWidth
                    value={formData.nombre}
                    onChange={(e) => handleInputChange('nombre', e.target.value)}
                    error={!!errors.nombre || !!nombreError}
                    helperText={errors.nombre || nombreError}
                    placeholder="Ej: Matrícula, Apertura, Seguro"
                  />

                  
                  <Typography fontWeight={600} sx={{ml: 1, mt: 2 }}>Aplica a</Typography>
                  <FormControl fullWidth error={!!errors.aplicaA}>
                    <Select 
                      sx={{backgroundColor: 'white'}}
                      value={formData.aplicaA}
                      onChange={(e) => handleInputChange('aplicaA', e.target.value)}
                      label="Aplica a *"
                    >
                      {OPciones.aplicaA.map(option => (
                        <MenuItem key={option.value} value={option.value}>
                          {option.label}
                        </MenuItem>
                      ))}
                    </Select>
                    {errors.aplicaA && (
                      <Typography variant="caption" color="error">
                        {errors.aplicaA}
                      </Typography>
                    )}
                  </FormControl>

                  {formData.aplicaA === 'prestamo' && (
                    <>
                      <Typography fontWeight={600} sx={{ ml: 1, mt: 2 }}>Tipo de cargo</Typography>
                      <FormControl fullWidth>
                        <Select
                          sx={{ backgroundColor: 'white' }}
                          value={formData.tipo}
                          onChange={(e) => handleInputChange('tipo', e.target.value)}
                        >
                          {OPciones.tipo.map((option) => (
                            <MenuItem key={option.value} value={option.value}>
                              {option.label}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                      <Typography variant="caption" color="text.secondary" sx={{ ml: 1, mt: 0.5 }}>
                        {formData.tipo === 'especifico'
                          ? 'Solo aparecerá al asignar manualmente desde el detalle de un préstamo.'
                          : 'Aparecerá en el catálogo de cargos al cobrar el préstamo.'}
                      </Typography>
                    </>
                  )}

                  {/* Si "Aplica a" es "prestamo", mostrar selección múltiple de productos financieros o categorías de producto */}
                  {formData.aplicaA === 'prestamo' ? (
                    <>
                      <Typography fontWeight={600} sx={{ ml: 1, mt: 2 }}>
                        Productos financieros
                      </Typography>
                      <FormControl fullWidth sx={{ mb: 2 }}>
                      <Selector
                        multiple
                        value={productosFinancierosSelected}
                        onChange={(e) => {
                          setProductosFinancierosSelected(e.target.value);
                        }}
                        options={productosFinancieros.map(producto => ({label:JSON.parse(producto.generales).nombre,value:producto.idprodfinanciero}))}
                        placeholder="Producto financiero"
                        error={!!errors.productosFinancieros}
                        helperText={errors.productosFinancieros}
                      />
                      </FormControl>

                    </>
                  ) :
                  <>
                      <Typography fontWeight={600} sx={{ ml: 1, mt: 2 }}>
                        Categorías de producto
                      </Typography>
                      <FormControl fullWidth>
                        <Selector
                          sx={{backgroundColor: 'white'}}
                          multiple
                          value={categoriasProductoSelected}
                          onChange={(e) => setCategoriasProductoSelected(e.target.value)}
                          options={categoriasProducto}
                          placeholder="Categoría"
                          error={!!errors.categoriasProducto}
                          helperText={errors.categoriasProducto}
                        >
                        </Selector>
                      </FormControl>

                  </>
                  }

                  {/* Monto fijo  */}
                  <Typography fontWeight={600} sx={{ml: 1, mt:1 }}>Monto</Typography>
                  <TextField
                      sx={{backgroundColor: 'white'}}
                      placeholder="Monto fijo"
                      fullWidth
                      value={formatNumberWithCommas(montoFijo)}
                      onChange={(e) => setMontoFijo(e.target.value.replace(/,/g, ''))}
                      error={!!errors.montoFijo}
                      helperText={errors.montoFijo}
                      InputProps={{
                        startAdornment: <InputAdornment position="start">{"RD$"}</InputAdornment>
                      }}
                    />

                  <Typography fontWeight={600} sx={{ml: 1, mt: 2 }}>Descripción</Typography>
                  <TextField
                    sx={{backgroundColor: 'white'}}
                    fullWidth
                    value={formData.descripcion}
                    onChange={(e) => handleInputChange('descripcion', e.target.value)}
                    placeholder="Descripción opcional de la categoría"
                  />



                  {/* Gestión operativa */}
                  <FormControlLabel
                    sx={{mt: 2, ml: 1,fontWeight: 600}}
                    control={
                      <Switch
                        checked={formData.requiereGestionOperativa}
                        onChange={(e) => handleInputChange('requiereGestionOperativa', e.target.checked)}
                      />
                    }
                    label="Requiere gestión operativa(Recepcion, Entrega, Transferencia)"
                  />

                </Stack>
              </Grid>

            </Grid>
          </LocalizationProvider>
        </DialogContent>

        <DialogActions sx={{ p: 3, pt: 1 }}>
          <Button onClick={handleClose} disabled={loading}>
            Cancelar
          </Button>
          <Button
            color='primarybutton'
            sx={{fontWeight: 600}}
            type="submit"
            variant="contained"
            disabled={loading || !!nombreError}
            onClick={() => onSubmit()}    
          >
            {loading ? 'Guardando...' : (isEditing ? 'Actualizar' : 'Crear')}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
}
